import { useEffect, useState } from "react";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  SelectChangeEvent,
  SelectProps,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllowedCaisses } from "../../reduxStore/ScopeSlice";
import { RootState, AppDispatch } from "../../reduxStore/Store";
import { GeneriqueData } from "../../models/models";

interface SelectCaisseProps
extends Omit<SelectProps<number | string>, "value" | "onChange"> {
  onCaisseChange: (generiqueData: GeneriqueData | null) => void;
  selectedCaisse?: GeneriqueData | null;
  /** Affiche une option « Toutes les caisses » (valeur vide) — utile en filtre */
  includeAllOption?: boolean;
  /** Override explicite du département filtré (sinon le département actif du contexte).
   *  Utile quand un formulaire cible un département choisi localement, différent du
   *  contexte de session (ex: création en mode "Tous départements"). */
  departement_id?: number | null;
}

const SelectCaisse: React.FC<SelectCaisseProps> = ({
  onCaisseChange,
  selectedCaisse,
  includeAllOption = false,
  departement_id,
  ...selectProps
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { allowedCaisses: caisseData, loading, error } = useSelector((state: RootState) => state.ScopeSlice);
  const { selectedDepartement } = useSelector((state: RootState) => state.AppSlice);
  const effectiveDeptId = departement_id !== undefined ? departement_id : (selectedDepartement?.id ?? null);
  const [caisseId, setCaisseId] = useState<number | string>("");

  useEffect(() => {
    dispatch(fetchAllowedCaisses({ departement_id: effectiveDeptId }));
  }, [dispatch, effectiveDeptId]);

  useEffect(() => {
    if (selectedCaisse) {
      setCaisseId(selectedCaisse.id);
    } else {
      // Sélection annulée (reset) → revient sur « Toutes »
      setCaisseId("");
    }
  }, [selectedCaisse]);

  const handleChange = (event: SelectChangeEvent<string | number>) => {
    const selectedId = event.target.value;
    const selectedCaisse = caisseData.find((item) => item.id === Number(selectedId)) || null;
    setCaisseId(selectedId);
    onCaisseChange(selectedCaisse);
  };

  return (
    <FormControl fullWidth size="small" variant="outlined" error={!!error}>
      <InputLabel id="select-caisse-label">Caisse</InputLabel>
      <Select
        labelId="select-caisse-label"
        value={caisseId}
        onChange={handleChange}
        label="Caisse"
        disabled={loading}
        size="small"
        {...selectProps}
      >
        {caisseData.length === 0 ? (
          <MenuItem value="">Aucune caisse trouvée</MenuItem>
        ) : (
          [
            includeAllOption && (
              <MenuItem key="__all__" value="">
                <em>Toutes les caisses</em>
              </MenuItem>
            ),
            ...caisseData.map((caisseItem) => (
              <MenuItem key={caisseItem.id} value={caisseItem.id}>
                {caisseItem.nom}
              </MenuItem>
            )),
          ]
        )}
      </Select>
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};

export default SelectCaisse;
